<template>
  <div class="mb-4 flex justify-end py-2">
    <LinkToCreate :to="{ name: 'userCreate' }">
      Add User
    </LinkToCreate>
  </div>

  <BaseCard class="relative">
    <div class="mb-2 flex w-full">
      <SearchInput v-model="rawSearch" />
    </div>
    <BaseTable
      v-model:ordering="ordering"
      :rows="data?.items ?? []"
      :columns="columns"
      :is-loading="isLoading"
      :get-row-class="getRowClass"
    >
      <template #cell-email="{ row, value }">
        <span v-if="!row.isActive" class="text-error mr-2">
          INACTIVE
        </span>

        <span
          v-if="row.id === currentUser?.id"
          class="text-info mr-2"
        >
          YOU
        </span>

        {{ value }}
      </template>

      <template #cell-actions="{ row }">
        <BaseMenu>
          <template #default>
            <BaseMenuItem
              :to="{
                name: 'userEdit',
                params: {
                  userId: row.id,
                },
              }"
            >
              Edit User
            </BaseMenuItem>

            <BaseMenuItem
              v-if="row.isActive"
              @click="onSendUserLoginLink(row.id)"
            >
              Send Portal Login Link
            </BaseMenuItem>

            <template v-if="row.id !== currentUser?.id">
              <BaseMenuItem
                v-if="row.isActive"
                @click="onDeactivateUser(row.id)"
              >
                <span class="text-error">
                  Deactivate User
                </span>
              </BaseMenuItem>

              <BaseMenuItem
                v-else
                @click="onActivateUser(row.id)"
              >
                <span class="text-primary">
                  Activate User
                </span>
              </BaseMenuItem>
            </template>
          </template>
        </BaseMenu>
      </template>
    </BaseTable>

    <div class="flex justify-end py-2">
      <BasePagination
        v-model="page"
        :page-count="pageCount"
      />
    </div>
  </BaseCard>

  <BaseConfirmation
    ref="activateUserConfirmationRef"
    title="Activate User"
    description="This will allow the user to log in to the Web Portal either via requesting a log in link or by using their password if it is set"
    confirm-label="Activate"
    is-positive
  />
  <BaseConfirmation
    ref="deactivateUserConfirmationRef"
    title="Deactivate User"
    confirm-label="Deactivate"
  >
    Deactivating a user will prevent them from being able to
    log into the web portal
    <br />
    <br />
    <div class="flex items-center text-sm">
      <IconHeroiconsOutlineExclamationCircle
        class="text-info mr-2 h-8 w-8"
      />
      A user can be reactivated either on this page, or by
      requesting a log in link through the
      {{ organizationConfig?.appName }}
      application
    </div>
  </BaseConfirmation>
</template>

<script setup lang="ts">
import type { TableColumn } from "@/components/BaseTable.vue"
import type {
  ListUsersFilter,
  ListUsersOrdering,
  ListUsersResponseItem,
} from "@/services/users/listUsers"
import type BaseConfirmation from "@/components/BaseConfirmation.vue"

const { organizationConfig } = useOrganizationConfig()
const { currentUser } = useCurrentUser()
const rawSearch = ref("")

const debouncedSearch = debouncedRef(rawSearch, 300)

const filters = computed<ListUsersFilter>(() => ({
  search: debouncedSearch.value.trim(),
}))

const { page, pageCount, pagination, resetPagination } =
  usePagination({
    count: computed(() => data.value?.count || 0),
    limit: 10,
  })

const ordering = ref<ListUsersOrdering | undefined>()

watch(
  () => filters,
  () => resetPagination(),
  { deep: true }
)

const query = reactiveComputed(() => ({
  ...filters.value,
  ordering: ordering.value,
  ...pagination.value,
}))

const { data, isLoading } = useUsers(query)

const now = useNow()
const columns: Array<TableColumn<ListUsersResponseItem>> = [
  {
    id: "email",
    header: "Email",
    getCellValue: (row) => row.email,
    isSortable: true,
    autoWidth: true,
  },
  {
    id: "name",
    header: "Name",
    getCellValue: (row) =>
      `${row.firstName} ${row.lastName}`,
    isSortable: true,
  },
  {
    id: "lastLogin",
    header: "Last Login",
    getCellValue: (row) =>
      row.lastLogin
        ? DateFns.formatDistance(row.lastLogin, now.value, {
            addSuffix: true,
          })
        : "",
    align: "right",
    autoWidth: true,
    isSortable: true,
  },
  {
    id: "createdAt",
    header: "Account Created",
    getCellValue: (row) =>
      row.createdAt
        ? DateFns.formatDistance(row.createdAt, now.value, {
            addSuffix: true,
          })
        : "",
    align: "right",
    autoWidth: true,
    isSortable: true,
  },

  {
    id: "actions",
    header: "",
    align: "right",
    autoWidth: true,
  },
]

const activateUserConfirmationRef = ref<InstanceType<
  typeof BaseConfirmation
> | null>(null)
const onActivateUser = async (
  userId: ListUsersResponseItem["id"]
) => {
  if (activateUserConfirmationRef.value === null) return

  const { isCanceled } =
    await activateUserConfirmationRef.value.reveal()
  if (isCanceled) return
  activateUser(userId)
}

const deactivateUserConfirmationRef = ref<InstanceType<
  typeof BaseConfirmation
> | null>(null)
const onDeactivateUser = async (
  userId: ListUsersResponseItem["id"]
) => {
  if (deactivateUserConfirmationRef.value === null) return

  const { isCanceled } =
    await deactivateUserConfirmationRef.value.reveal()
  if (isCanceled) return
  deactivateUser(userId)
}

const onSendUserLoginLink = (
  userId: ListUsersResponseItem["id"]
) => sendUserLoginLink(userId)

const getRowClass = (row: ListUsersResponseItem) => {
  return !row.isActive ? "text-background-fg/30" : ""
}
</script>
